import type { CSSProperties } from 'react';

export interface LogoProps {
  /** pixel box, applied to width & height (viewBox stays 64×64) */
  size?: number;
  className?: string;
  style?: CSSProperties;
  /** accessible name; also rendered as <title> */
  title?: string;
}

/**
 * The canonical Nomad Agent mark (NOMAD_LOGO_SVG): onyx rounded square with
 * dashed compass ring, N monogram, and 4-point star in crisp #FAFAFA.
 */
export function Logo({ size = 24, className, style, title = 'Nomad Tabs' }: LogoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      className={className}
      style={{ flexShrink: 0, borderRadius: Math.round(size * 0.25), ...style }}
      role="img"
      aria-label={title}
    >
      <title>{title}</title>
      <rect x="2" y="2" width="60" height="60" rx="14" fill="#09090B" stroke="#27272A" strokeWidth="1.5" />
      <circle cx="32" cy="32" r="21" stroke="#3F3F46" strokeWidth="1" strokeDasharray="2 3" />
      <path
        d="M19 46V18L32 32M45 18V46L32 32"
        stroke="#FAFAFA"
        strokeWidth="4.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M32 13L35.2 28.8L51 32L35.2 35.2L32 51L28.8 35.2L13 32L28.8 28.8L32 13Z"
        fill="#FAFAFA"
      />
      <circle cx="32" cy="32" r="2.6" fill="#09090B" />
    </svg>
  );
}

export interface WordmarkProps {
  /** wordmark font-size in px */
  size?: number;
  /** render the Logo mark before the text */
  showMark?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** "Nomad Tabs" set in Inter 700, −0.02em — optionally preceded by the Nomad mark. */
export function Wordmark({ size = 15, showMark = false, className, style }: WordmarkProps) {
  return (
    <span
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...style }}
    >
      {showMark && <Logo size={Math.round(size * 1.4)} />}
      <span
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 700,
          fontSize: size,
          letterSpacing: '-.02em',
          color: 'var(--text-1)',
          lineHeight: 1,
        }}
      >
        Nomad Tabs
      </span>
    </span>
  );
}
